import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { FileText, ExternalLink } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { AdminCursorTable } from "@/components/dashboard/AdminCursorTable";
import { useServerFn } from "@tanstack/react-start";
import { getPressStats, listPressPages } from "@/lib/seo-tabs.functions";

export const Route = createFileRoute("/dashboard/admin/seo-press")({
  head: () => ({ meta: [{ title: "Press · Admin" }, { name: "robots", content: "noindex" }] }),
  component: PressPage,
});

type Stats = Awaited<ReturnType<typeof getPressStats>>;
type Row = Awaited<ReturnType<typeof listPressPages>>["rows"][number];

function PressPage() {
  const fetchStats = useServerFn(getPressStats);
  const fetchList = useServerFn(listPressPages);
  const [stats, setStats] = useState<Stats | null>(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [indexable, setIndexable] = useState<"all" | "yes" | "no">("all");

  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
  }, [fetchStats]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const resetKey = JSON.stringify({ indexable, q: debounced });

  return (
    <AdminPageShell
      title="Press releases"
      description="Distribution, pickups, and backlinks from press coverage."
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder="Filter by slug"
      stats={[
        { label: "Pages", value: stats?.total.toLocaleString() ?? "—" },
        { label: "Indexed", value: stats?.indexed.toLocaleString() ?? "—" },
        { label: "Noindex", value: stats?.noindex.toLocaleString() ?? "—" },
      ]}
      primaryAction={{ label: "New release", icon: FileText }}
    >
      <div className="mb-3 flex items-center gap-2">
        {(["all", "yes", "no"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setIndexable(s)}
            className={`h-8 px-3 text-[12px] border rounded-md ${
              indexable === s
                ? "border-primary text-primary font-semibold"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {s === "all" ? "All" : s === "yes" ? "Indexable" : "Noindex"}
          </button>
        ))}
      </div>

      <AdminCursorTable<Row, string>
        resetKey={resetKey}
        columns={[
          { label: "Slug" },
          { label: "Title" },
          { label: "Providers" },
          { label: "Indexable" },
          { label: "Refreshed" },
          { label: "" },
        ]}
        rowKey={(r) => r.id}
        fetchPage={async (cursor) =>
          fetchList({
            data: { cursor: cursor as string | null, indexable, q: debounced || undefined },
          })
        }
        renderRow={(r) => (
          <>
            <td className="p-3 font-mono text-xs">{r.slug}</td>
            <td className="p-3 text-[12px] max-w-[420px] truncate" title={r.title ?? ""}>
              {r.title ?? "—"}
            </td>
            <td className="p-3 text-[12px]">{r.provider_count ?? 0}</td>
            <td className="p-3 text-[12px]">
              <span className={r.is_indexable ? "text-primary" : "text-muted-foreground"}>
                {r.is_indexable ? "yes" : "no"}
              </span>
            </td>
            <td className="p-3 text-[11px] text-muted-foreground whitespace-nowrap">
              {r.last_refreshed_at ? r.last_refreshed_at.slice(0, 16).replace("T", " ") : "—"}
            </td>
            <td className="p-3 text-right">
              <a
                href={`/${r.slug}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center text-xs px-2 py-1 border border-border rounded-md hover:bg-muted"
              >
                <ExternalLink className="h-3 w-3 mr-1" /> View
              </a>
            </td>
          </>
        )}
        emptyMessage="No press pages yet."
      />
    </AdminPageShell>
  );
}
